import { useEffect, useState } from "react";
import FadeIn from "react-fade-in";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { A11y, Pagination, Navigation, Thumbs } from "swiper/core";

import { arrayOf, node, shape, string, bool } from "prop-types";
import { nextIcon, prevIcon } from "../../common/icons.jsx";
import RichText from "../RichText/RichText.jsx";

import cx from "classnames";
import useMatchMedia from "../../common/useMatchMedia.js";

const propTypes = {
  title: string,
  description: node,
  slides: arrayOf(
    shape({
      image: shape({
        mobile1X: string.isRequired,
        mobile2X: string.isRequired,
        desktop1X: string.isRequired,
        desktop2X: string.isRequired,
        alt: string,
        caption: node,
      }),
      video: shape({
        src: string.isRequired,
        caption: node,
        autoplaySoundOff: bool,
        autoPlaySoundOn: bool,
        loop: bool,
        thumbnailImage: string,
      }),
    }),
  ),
};

const MultimediaGallery = (props) => {
  const { title, description, slides } = props;

  const [renderSwipers, setRenderSwipers] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const matchesLarge = useMatchMedia("(min-width: 1024px)");

  useEffect(() => {
    // Required to let text load and prevent swiper init errors
    setActiveIndex(0);

    setRenderSwipers(true);
  }, []);

  SwiperCore.use([A11y, Pagination, Navigation, Thumbs]);

  return (
    <section className="multimedia-gallery">
      <div className="container container--100">
        {title && (
          <div className="component__header">
            <RichText>
              <h2>{title}</h2>
            </RichText>
          </div>
        )}

        {description && (
          <div className="component__description">
            <RichText>
              <div dangerouslySetInnerHTML={{ __html: description }}></div>
            </RichText>
          </div>
        )}
      </div>

      <div className="multimedia-gallery__wrapper container--bleed">
        {renderSwipers && (
          <Swiper
            className="multimedia-gallery__swiper-main"
            onSlideChange={(swiper) => {
              const prevSlide = swiper.slides[swiper.previousIndex];
              const prevSlideVideo = prevSlide.querySelector("video");

              const currentSlide = swiper.slides[swiper.activeIndex];
              const currentSlideVideo = currentSlide.querySelector("video");

              if (prevSlideVideo) {
                prevSlideVideo.pause();
              }

              if (currentSlideVideo) {
                currentSlideVideo.currentTime = 0;
                currentSlideVideo.play();
              }

              setActiveIndex(swiper.realIndex);
            }}
            freeMode={false}
            slidesPerView={1}
            a11y
            observer={true}
            observeParents={true}
            spaceBetween={30}
            loop={true}
            navigation={{
              prevEl: ".js-carousel-prev",
              nextEl: ".js-carousel-next",
            }}
            thumbs={{ swiper: thumbsSwiper }}
          >
            {slides.map((slide, i) => {
              return (
                <>
                  <SwiperSlide key={i}>
                    <div
                      className={cx("multimedia-gallery__slide", {
                        "multimedia-gallery__slide--video": slide.video,
                      })}
                    >
                      {slide.video && (
                        <>
                          <div className="multimedia-gallery__slide-image">
                            <video
                              width="100%"
                              height="auto"
                              controls
                              autoPlay={
                                slide.video.autoplaySoundOff ||
                                slide.video.autoPlaySoundOn
                              }
                              muted={slide.video.autoplaySoundOff}
                              loop={slide.video.loop}
                              aria-describedby={slide.video.caption ?? ""}
                            >
                              <track default kind="captions" srcLang="en" />
                              <source src={slide.video.src} type="video/mp4" />

                              <p>
                                Your browser doesn't support HTML5 video. Here
                                is a{" "}
                                <a href={slide.video.src}>link to the video</a>{" "}
                                instead.
                              </p>
                            </video>
                          </div>
                        </>
                      )}

                      {slide.image && (
                        <>
                          <div className="multimedia-gallery__slide-image">
                            <picture>
                              <source
                                srcSet={`${slide.image.desktop1X} 1366w, ${slide.image.desktop2X} 2732w`}
                                media="(min-width: 746px)"
                              />
                              <img
                                alt="hero img"
                                aria-describedby={slide.image.caption ?? ""}
                                src={slide.image.mobile1X}
                                srcSet={`${slide.image.mobile1X} 414w, ${slide.image.mobile2X} 640w`}
                              />
                            </picture>
                          </div>

                          {slide.image.caption && activeIndex === i && (
                            <FadeIn>
                              <div className="multimedia-gallery__slide-caption">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: slide.image.caption,
                                  }}
                                ></div>
                              </div>
                            </FadeIn>
                          )}
                        </>
                      )}
                    </div>
                  </SwiperSlide>
                </>
              );
            })}
          </Swiper>
        )}

        {renderSwipers &&
          slides.map((slide, i) => {
            return (
              <div className="container container--100" key={i}>
                {slide.image?.caption && activeIndex === i && (
                  <div
                    className="multimedia-gallery__caption--mobile"
                    dangerouslySetInnerHTML={{
                      __html: slide.image?.caption,
                    }}
                  ></div>
                )}
              </div>
            );
          })}

        <div
          className={cx("multimedia-gallery__footer", {
            "container container--100": !matchesLarge,
          })}
        >
          <button
            aria-label="Previous slide"
            className="js-carousel-prev multimedia-gallery__prev"
          >
            {prevIcon}
          </button>

          {renderSwipers && (
            <Swiper
              className={cx(
                "multimedia-gallery__swiper-thumbs container container--100",
                {
                  "multimedia-gallery__swiper-thumbs--centered":
                    slides.length < 6,
                },
              )}
              onSlideChange={(s) => {
                setActiveIndex(s.realIndex);
              }}
              onSwiper={setThumbsSwiper}
              loop={slides.length < 6 ? false : true}
              a11y
              observer={true}
              observeParents={true}
              spaceBetween={30}
              slidesPerView={slides.length < 6 ? slides.length : 6}
              preventClicks={false}
              preventClicksPropagation={false}
              freeMode={false}
            >
              {slides.map((slide, i) => {
                return (
                  <SwiperSlide key={i}>
                    <div className="multimedia-gallery__thumb-image">
                      <picture>
                        {slide.image && (
                          <>
                            <source
                              srcSet={`${slide.image?.desktop1X} 1x, ${slide.image?.desktop2X} 2x`}
                              media="(min-width: 1060px)"
                            />
                            <img
                              alt={slide.image?.alt ? slide.image?.alt : ""}
                              src={slide.image?.mobile1X}
                              srcSet={`${slide.image?.mobile1X} 414w, ${slide.image?.mobile2X} 640w`}
                            />
                          </>
                        )}
                        {slide.video && (
                          <>
                            {/* <source
                            srcSet={`${slide.image?.desktop1X} 1x, ${slide.image?.desktop2X} 2x`}
                            media="(min-width: 1060px)"
                            /> */}
                            <img
                              alt={""}
                              src={slide.video?.thumbnailImage}
                              // srcSet={`${slide.image?.mobile1X} 414w, ${slide.image?.mobile2X} 640w`}
                            />
                          </>
                        )}
                      </picture>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}

          <div className="multimedia-gallery__counter" aria-hidden>
            {activeIndex + 1} / {slides?.length}
          </div>

          <button
            aria-label="Next slide"
            className="js-carousel-next multimedia-gallery__next"
          >
            {nextIcon}
          </button>
        </div>
      </div>
    </section>
  );
};

MultimediaGallery.propTypes = propTypes;
export default MultimediaGallery;
